import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const apiBase = process.env.NODE_ENV === 'production' 
  ? 'https://proofofprice.com' 
  : 'http://localhost:3001';

const Container = styled.div`
  padding: 20px;
  background: ${props => props.theme?.surface || '#2d3748'};
  border-radius: 8px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 15px;
`;

const Title = styled.h2`
  color: ${props => props.theme?.text || '#e2e8f0'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusBadge = styled.span<{ enabled: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  background: ${props => props.enabled ? '#48BB7820' : '#F5656520'};
  color: ${props => props.enabled ? '#48BB78' : '#F56565'};
  border: 1px solid ${props => props.enabled ? '#48BB78' : '#F56565'};
`;

const ToggleButton = styled.button<{ enabled: boolean }>`
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  ${props => props.enabled ? `
    background: #F56565;
    color: white;
    &:hover { background: #E53E3E; }
  ` : `
    background: #48BB78;
    color: white;
    &:hover { background: #38A169; }
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 15px;
  margin-bottom: 25px;
`;

const StatCard = styled.div`
  background: ${props => props.theme?.background || '#1a202c'};
  padding: 15px;
  border-radius: 8px;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme?.primary || '#3182ce'};
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme?.subText || '#a0aec0'};
  text-transform: uppercase;
`;

const SettingsSection = styled.div`
  background: ${props => props.theme?.background || '#1a202c'};
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme?.border || '#4a5568'};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SettingLabel = styled.div`
  color: ${props => props.theme?.text || '#e2e8f0'};
  font-weight: 500;
`;

const SettingDescription = styled.div`
  color: ${props => props.theme?.subText || '#a0aec0'};
  font-size: 12px;
  margin-top: 2px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  background: ${props => props.theme?.surface || '#2d3748'};
  color: ${props => props.theme?.text || '#e2e8f0'};
  border: 1px solid ${props => props.theme?.border || '#4a5568'};
  cursor: pointer;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid ${props => props.theme?.border || '#4a5568'};
  background: ${props => props.theme?.surface || '#2d3748'};
  color: ${props => props.theme?.text || '#e2e8f0'};
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.theme?.primary || '#3182ce'};
    color: white;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActivityLog = styled.div`
  max-height: 400px;
  overflow-y: auto;
  background: ${props => props.theme?.background || '#1a202c'};
  border-radius: 8px;
  padding: 15px;
`;

const LogEntry = styled.div<{ hasPrediction?: boolean }>`
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  background: ${props => props.theme?.surface || '#2d3748'};
  border-left: 3px solid ${props => props.hasPrediction ? '#48BB78' : '#4a5568'};
`;

const LogTime = styled.span`
  color: ${props => props.theme?.subText || '#a0aec0'};
  font-size: 12px;
`;

const LogUser = styled.span`
  color: ${props => props.theme?.primary || '#3182ce'};
  font-weight: 500;
`;

const CostDisplay = styled.div`
  background: #ECC94B20;
  border: 1px solid #ECC94B;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
`;

const CostTitle = styled.div`
  color: #ECC94B;
  font-weight: 600;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CostBreakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  color: ${props => props.theme?.text || '#e2e8f0'};
  font-size: 14px;
`;

interface MentionMonitorStats {
  enabled: boolean;
  pollIntervalMinutes: number;
  autoReply: boolean;
  lastPoll: string | null;
  totalPolls: number;
  totalReplies: number;
  estimatedCost: number;
  totalProcessed: number;
  predictionsFound: number;
  repliesSent: number;
  last24Hours: number;
}

interface ProcessedMention {
  id: number;
  tweet_id: string;
  author_username: string;
  tweet_text: string;
  has_prediction: boolean;
  replied: boolean;
  processed_at: string;
}

const MentionMonitorPanel: React.FC = () => {
  const [stats, setStats] = useState<MentionMonitorStats | null>(null);
  const [activity, setActivity] = useState<ProcessedMention[]>([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/x/mention-monitor/status`);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchActivity = async () => {
    try {
      const response = await axios.get(`${apiBase}/api/x/mention-monitor/activity?limit=20`);
      if (response.data.success) {
        setActivity(response.data.activity || []);
      }
    } catch (error) {
      console.error('Failed to fetch activity:', error);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchStats(), fetchActivity()]);
      setLoading(false);
    };
    load();
    
    // Refresh stats every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const toggleEnabled = async () => {
    if (!stats) return;
    setSaving(true);
    try {
      const response = await axios.post(`${apiBase}/api/x/mention-monitor/toggle`, {
        enabled: !stats.enabled
      });
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to toggle:', error);
    }
    setSaving(false);
  };

  const updateSettings = async (settings: Partial<{ pollIntervalMinutes: number; autoReply: boolean }>) => {
    setSaving(true);
    try {
      const response = await axios.post(`${apiBase}/api/x/mention-monitor/settings`, settings);
      if (response.data.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
    setSaving(false);
  };

  const triggerPoll = async () => {
    setPolling(true);
    try {
      const response = await axios.post(`${apiBase}/api/x/mention-monitor/poll`);
      if (response.data.success) {
        setStats(response.data.stats);
        await fetchActivity();
      }
    } catch (error: any) {
      alert(error.response?.data?.error || 'Poll failed');
    }
    setPolling(false);
  };

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      </Container>
    );
  }

  const estimatedMonthlyCost = stats ? (
    (stats.pollIntervalMinutes > 0 ? (60 / stats.pollIntervalMinutes) * 24 * 30 * 0.005 : 0) + 
    (stats.totalReplies > 0 ? (stats.totalReplies / Math.max(1, stats.totalPolls)) * (60 / stats.pollIntervalMinutes) * 24 * 30 * 0.01 : 0)
  ).toFixed(2) : '0.00';

  return (
    <Container>
      <Header>
        <Title>
          🔔 Mention Monitor
          <StatusBadge enabled={stats?.enabled || false}>
            {stats?.enabled ? '● ACTIVE' : '○ DISABLED'}
          </StatusBadge>
        </Title>
        <ToggleButton 
          enabled={stats?.enabled || false}
          onClick={toggleEnabled}
          disabled={saving}
        >
          {stats?.enabled ? '⏹️ Disable Monitoring' : '▶️ Enable Monitoring'}
        </ToggleButton>
      </Header>

      <CostDisplay>
        <CostTitle>💰 Cost Tracking (Pay-Per-Use)</CostTitle>
        <CostBreakdown>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 600 }}>${stats?.estimatedCost.toFixed(4) || '0.0000'}</div>
            <div style={{ fontSize: '12px', opacity: 0.7 }}>Total Spent</div>
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 600 }}>~${estimatedMonthlyCost}</div>
            <div style={{ fontSize: '12px', opacity: 0.7 }}>Est. Monthly</div>
          </div>
          <div>
            <div style={{ fontSize: '20px', fontWeight: 600 }}>${(0.005).toFixed(3)}/poll</div>
            <div style={{ fontSize: '12px', opacity: 0.7 }}>Per Poll Cost</div>
          </div>
        </CostBreakdown>
      </CostDisplay>

      <StatsGrid>
        <StatCard>
          <StatValue>{stats?.totalPolls || 0}</StatValue>
          <StatLabel>Total Polls</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats?.totalProcessed || 0}</StatValue>
          <StatLabel>Mentions Processed</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats?.predictionsFound || 0}</StatValue>
          <StatLabel>Predictions Found</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats?.repliesSent || 0}</StatValue>
          <StatLabel>Auto-Replies Sent</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats?.last24Hours || 0}</StatValue>
          <StatLabel>Last 24 Hours</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{stats?.lastPoll ? new Date(stats.lastPoll).toLocaleTimeString() : 'Never'}</StatValue>
          <StatLabel>Last Poll</StatLabel>
        </StatCard>
      </StatsGrid>

      <SettingsSection>
        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>⚙️ Settings</h3>
        
        <SettingsRow>
          <div>
            <SettingLabel>Poll Interval</SettingLabel>
            <SettingDescription>How often to check for new mentions</SettingDescription>
          </div>
          <Select 
            value={stats?.pollIntervalMinutes || 60}
            onChange={(e) => updateSettings({ pollIntervalMinutes: parseInt(e.target.value) })}
            disabled={saving}
          >
            <option value={15}>Every 15 minutes (~$7.20/month)</option>
            <option value={30}>Every 30 minutes (~$3.60/month)</option>
            <option value={60}>Every hour (~$1.80/month)</option>
            <option value={120}>Every 2 hours (~$0.90/month)</option>
            <option value={360}>Every 6 hours (~$0.30/month)</option>
            <option value={720}>Every 12 hours (~$0.15/month)</option>
          </Select>
        </SettingsRow>

        <SettingsRow>
          <div>
            <SettingLabel>Auto-Reply</SettingLabel>
            <SettingDescription>Automatically reply to mentions containing predictions</SettingDescription>
          </div>
          <Checkbox 
            type="checkbox"
            checked={stats?.autoReply || false}
            onChange={(e) => updateSettings({ autoReply: e.target.checked })}
            disabled={saving}
          />
        </SettingsRow>

        <SettingsRow>
          <div>
            <SettingLabel>Manual Poll</SettingLabel>
            <SettingDescription>Trigger an immediate check for mentions</SettingDescription>
          </div>
          <ActionButton 
            onClick={triggerPoll}
            disabled={polling || !stats?.enabled}
          >
            {polling ? '⏳ Polling...' : '🔄 Poll Now'}
          </ActionButton>
        </SettingsRow>
      </SettingsSection>

      <h3 style={{ marginBottom: '15px' }}>📜 Recent Activity</h3>
      <ActivityLog>
        {activity.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', opacity: 0.6 }}>
            No activity yet. Enable monitoring to start tracking mentions.
          </div>
        ) : (
          activity.map((item) => (
            <LogEntry key={item.id} hasPrediction={item.has_prediction}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <LogUser>@{item.author_username}</LogUser>
                <LogTime>{new Date(item.processed_at).toLocaleString()}</LogTime>
              </div>
              <div style={{ fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>
                {item.tweet_text?.substring(0, 200)}...
              </div>
              <div style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                {item.has_prediction && (
                  <span style={{ color: '#48BB78' }}>✅ Prediction detected</span>
                )}
                {item.replied && (
                  <span style={{ color: '#3182ce' }}>💬 Replied</span>
                )}
                {!item.has_prediction && (
                  <span style={{ opacity: 0.5 }}>No prediction</span>
                )}
              </div>
            </LogEntry>
          ))
        )}
      </ActivityLog>
    </Container>
  );
};

export default MentionMonitorPanel;
